import { type Detection, detectTemplate } from "./parser";
import { type Template, type TemplateKey, TEMPLATES } from "./templates";

export interface PromptInputs {
  subject?: string;
  action?: string;
  location?: string;
  element?: string;
  mood?: string;
}

export const NO_TEXT_INSTRUCTION =
  " The image must contain absolutely no text, no letters, no words, no numbers, no writing, no signage, no watermarks, and no typographic elements of any kind. Purely visual, zero text.";

function resolveMood(tmpl: Template, moodValue?: string): string | null {
  if (!moodValue) {
    return null;
  }
  return (tmpl.moodMap as unknown as Record<string, string>)[moodValue] ?? null;
}

export function generate(templateKey: TemplateKey, inputs: PromptInputs): string {
  const tmpl = TEMPLATES[templateKey] as Template | undefined;
  if (!tmpl) {
    return "Error: Unknown template.";
  }

  let prompt = tmpl.template;

  const values: Record<string, string> = {
    SUBJECT: inputs.subject || tmpl.defaults.SUBJECT,
    ACTION: inputs.action || tmpl.defaults.ACTION,
    LOCATION: inputs.location || tmpl.defaults.LOCATION || "an evocative setting",
    ELEMENT: inputs.element || tmpl.defaults.ELEMENT,
    ENVIRONMENTAL_ELEMENT: tmpl.defaults.ENVIRONMENTAL_ELEMENT || "",
    CITYSCAPE: tmpl.defaults.CITYSCAPE || "",
    COLOR_PALETTE: tmpl.defaults.COLOR_PALETTE,
    LIGHT_DESCRIPTION: tmpl.defaults.LIGHT_DESCRIPTION,
    COMPOSITION_RULE: tmpl.defaults.COMPOSITION_RULE,
    MOOD_DESCRIPTION: resolveMood(tmpl, inputs.mood) || tmpl.defaults.MOOD_DESCRIPTION,
  };

  for (const [key, value] of Object.entries(values)) {
    // Replacer function avoids `$` sequences in values being interpreted.
    prompt = prompt.replace(new RegExp(`\\[${key}\\]`, "g"), () => value);
  }

  // Clean unreplaced placeholders
  prompt = prompt.replace(/\[[\w_]+\]/g, "");

  // Normalize whitespace
  prompt = prompt.replace(/\s{2,}/g, " ").trim();

  // Append no-text instruction to prevent gibberish text in generated images
  return prompt + NO_TEXT_INSTRUCTION;
}

export function autoDetectFromStructured(inputs: PromptInputs): Detection {
  const combined = [inputs.subject, inputs.action, inputs.location, inputs.element]
    .filter(Boolean)
    .join(" ");
  return detectTemplate(combined);
}

/** Append refinement feedback, keeping the no-text instruction last. */
export function refinePrompt(prompt: string, feedback: string): string {
  const base = prompt.endsWith(NO_TEXT_INSTRUCTION)
    ? prompt.slice(0, -NO_TEXT_INSTRUCTION.length)
    : prompt;
  return `${base} IMPORTANT MODIFICATIONS: ${feedback}.${NO_TEXT_INSTRUCTION}`;
}
